import React, { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { Refresh as RefreshIcon, WarehouseOutlined as WarehouseIcon } from '@mui/icons-material'

import { StandardPage } from '../../components/_EOS/StandardPage'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useApi } from '../../hooks/useApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { useWarehouses } from '../../hooks/useWarehouses'
import { Status } from '../../hooks/useFetchState'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { ComponentProps, FilterOption } from '../../components/_WMS/FilterComponent/types'
import { GridResponse } from './types'
import { clearLayoutCache, getLayoutGrid } from './api'
import {
  BRAND_PALETTE,
  MAP_MODE_LABELS,
  ProductsCellRef,
  ViewMode,
} from './config/constants'
import { LayoutGrid } from './components/LayoutGrid'
import { MapLegend } from './components/MapLegend'
import { AisleDetailModal } from './components/AisleDetailModal'
import { ProductsModal } from './components/ProductsModal'
import { layoutMapStyles as styles } from './styles/LayoutMapPage.styles'

/**
 * Marcas disponibles para el filtro de marca.
 *
 * Se expone por contexto y no por props porque `FilterComponent` sólo pasa
 * `value`/`onChange` a los componentes de tipo `custom`. `BrandSelectInput`
 * debe además mantener una identidad estable entre renders: si se define
 * dentro de `LayoutMapPage`, cada render crea un componente nuevo y el input
 * se desmonta mientras el usuario interactúa con él.
 */
const BrandsContext = createContext<string[]>([])

function BrandSelectInput({ value, onChange }: ComponentProps) {
  const brands = useContext(BrandsContext)

  return (
    <TextField
      select
      value={value || ''}
      onChange={onChange}
      variant='outlined'
      fullWidth
      size='small'
      sx={styles.brandSelect}
    >
      <MenuItem value=''>Seleccionar marca...</MenuItem>
      {brands.map((name) => (
        <MenuItem key={name} value={name}>{name}</MenuItem>
      ))}
    </TextField>
  )
}


interface AppliedQuery {
  warehouseId: number
  brand: string | null
}

const REFRESH_COOLDOWN_SECONDS = 300
const REFRESH_TS_KEY = 'layoutmap_refresh_ts'

function readLastRefresh(): number {
  return Number(localStorage.getItem(REFRESH_TS_KEY)) || 0
}

/** Permite mostrar hace cuánto fue la última ejecución de la consulta */
function formatAge(generatedAt: string, nowMs: number): string {
  const minutes = Math.floor((nowMs - new Date(generatedAt).getTime()) / 60000)
  if (minutes < 1) return 'hace menos de un minuto'
  if (minutes === 1) return 'hace 1 minuto'
  if (minutes < 60) return `hace ${minutes} minutos`
  const hours = Math.floor(minutes / 60)
  if (hours === 1) return 'hace 1 hora'
  if (hours < 24) return `hace ${hours} horas`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'hace 1 día' : `hace ${days} días`
}

function formatCooldown(seconds: number): string {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

export function LayoutMapPage() {
  const { warehousesState, getWarehouses } = useWarehouses()
  const { state: gridState, callApi: fetchGrid } = useApi<GridResponse>()
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const [appliedQuery, setAppliedQuery] = useState<AppliedQuery | null>(null)
  const [selectedAisle, setSelectedAisle] = useState<string | null>(null)
  const [productsCell, setProductsCell] = useState<ProductsCellRef | null>(null)
  const [mapMode, setMapMode] = useState<ViewMode>('brand')

  const activeWarehouses = useMemo(
    () => (warehousesState.warehouses ?? []).filter((w) => w.active),
    [warehousesState.warehouses]
  )

  useEffect(() => {
    if (!warehousesState.warehouses?.length) getWarehouses()
  }, [])

  // Derivados de la consulta aplicada — no se guardan en estado aparte.
  const appliedWarehouseId = appliedQuery?.warehouseId ?? null
  const highlightBrand = appliedQuery?.brand ?? null
  const hasWarehouseChip = selectedFilters.some((f) => f.key === 'warehouseId')

  const appliedWarehouseName = useMemo(
    () => activeWarehouses.find((w) => w.id === appliedWarehouseId)?.warehouseName ?? '',
    [activeWarehouses, appliedWarehouseId]
  )

    // Depende del objeto y no de sus campos: `handleSearch` crea uno nuevo en
    // cada BUSCAR, así que el usuario siempre re-consulta, aunque no haya
    // cambiado los filtros.
  useEffect(() => {
    if (!appliedQuery) return
      
    fetchGrid(getLayoutGrid(appliedQuery.warehouseId, appliedQuery.brand)).catch(() => {})
  }, [appliedQuery])

  const gridData = gridState.data

  const brandColors = useMemo<Map<string, string>>(() => {
    if (!gridData) return new Map()
    const names = Array.from(
      new Set(gridData.cells.flatMap((c) => c.brands.map((b) => b.name)))
    ).sort()
    const map = new Map<string, string>()
    names.forEach((name, i) => map.set(name, BRAND_PALETTE[i % BRAND_PALETTE.length]))
    return map
  }, [gridData])

  const zoneColors = useMemo<Map<string, string>>(() => {
    if (!gridData) return new Map()
    const names = Array.from(new Set(gridData.cells.flatMap((c) => c.zones ?? []))).sort()
    const map = new Map<string, string>()
    names.forEach((name, i) => map.set(name, BRAND_PALETTE[i % BRAND_PALETTE.length]))
    return map
  }, [gridData])

  const brandNames = useMemo(() => Array.from(brandColors.keys()), [brandColors])

  const filterOptions = useMemo<FilterOption[]>(() => {
    const opts: FilterOption[] = [
      {
        order: 1,
        key: 'warehouseId',
        label: 'Almacén',
        type: 'precreated',
        options: activeWarehouses.map((w) => ({ label: w.warehouseName, value: w.id })),
      },
    ]
    

    if (brandNames.length > 0 && hasWarehouseChip) {
      opts.push({ order: 2, key: 'brand', label: 'Marca', type: 'custom', Component: BrandSelectInput })
    }
    return opts
  }, [activeWarehouses, brandNames, hasWarehouseChip])

  /**
   * Hace obligatorio el almacén sin tocar `FilterComponent`.   
   */
  const handleFiltersChange = useCallback((value: React.SetStateAction<Filter[]>) => {
    setSelectedFilters((prev) => {
      const next = typeof value === 'function' ? value(prev) : value
      if (!next.some((f) => f.key === 'warehouseId')) {
        return next.filter((f) => f.key !== 'brand')
      }
      return next
    })
  }, [])

  const handleSearch = (filters: Filter[]) => {
    const warehouseFilter = filters.find((f) => f.key === 'warehouseId')
    if (!warehouseFilter) {
      setAppliedQuery(null)
      return
    }
    const brandFilter = filters.find((f) => f.key === 'brand')
    setAppliedQuery({
      warehouseId: Number(warehouseFilter.value),
      brand: brandFilter ? String(brandFilter.value) : null,
    })
  }

  const handleRefresh = useCallback(async () => {
    if (appliedWarehouseId === null) return

    try {
      await clearLayoutCache(appliedWarehouseId)
    } catch (error) {      
      openSnackBar('No se pudo limpiar la caché: los datos pueden no estar actualizados', false)
    }

    // Se recarga respetando el filtro de marca vigente.
    fetchGrid(getLayoutGrid(appliedWarehouseId, highlightBrand)).catch(() => {})    
  }, [appliedWarehouseId, highlightBrand])

  
  const handleAisleClick = useCallback((aisle: string) => setSelectedAisle(aisle), [])
  const handleCellClick = useCallback((ref: ProductsCellRef) => setProductsCell(ref), [])

  const showMap = Boolean(gridData) && appliedQuery !== null && hasWarehouseChip
  const isLoading = gridState.status === Status.Loading
  const isError = gridState.status === Status.Error

  return (
    <Fragment>
      <StandardPage pageTitle=''>        
        <BrandsContext.Provider value={brandNames}>
          <FilterComponent
            options={filterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={handleFiltersChange}
            onSearchClick={handleSearch}
            includeAddButton={true}
            includeSearchOnClean={true}
            requiredFilterKeys={['warehouseId']}
          />
        </BrandsContext.Provider>

        {isLoading && (
          <Box sx={styles.loadingWrapper}>
            <Box component='span' sx={styles.spinner} />
          </Box>
        )}

        {isError && (
          <Typography color='error' sx={styles.errorText}>
            {gridState.error ?? 'Error al cargar el mapa de layout'}
          </Typography>
        )}

        {!showMap && !isLoading && !isError && (
          <Box sx={styles.emptyState}>
            <Box sx={styles.emptyStateIconCircle}>
              <WarehouseIcon sx={styles.emptyStateIcon} />
            </Box>
            <Typography variant='h6' fontWeight={600} color='text.secondary' sx={styles.emptyStateTitle}>
              Ningún almacén seleccionado
            </Typography>
            <Typography variant='body2' color='text.disabled' sx={styles.emptyStateText}>
              Selecciona un almacén en el filtro y presiona <strong>BUSCAR</strong> para visualizar el mapa de layout
            </Typography>
          </Box>
        )}

        {showMap && gridData && (
          <Fragment>            
            <Box sx={styles.controlsBar}>
              <Box sx={styles.controlsGroup}>
                <WarehouseIcon sx={styles.warehouseIcon} />
                <Typography
                  variant='body2'
                  fontWeight={700}
                  title={appliedWarehouseName}
                  sx={styles.warehouseName}
                >
                  {appliedWarehouseName}
                </Typography>

                <Box sx={styles.verticalDivider} />
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                  sx={styles.modeLabel}
                >
                  Modo
                </Typography>
                {(Object.keys(MAP_MODE_LABELS) as ViewMode[]).map((m) => (
                  <Button
                    key={m}
                    size='small'
                    variant={mapMode === m ? 'contained' : 'outlined'}
                    onClick={() => setMapMode(m)}
                    sx={styles.modeButton(mapMode === m)}
                  >
                    {MAP_MODE_LABELS[m]}
                  </Button>
                ))}
              </Box>

              <Box sx={styles.metricsGroup}>
                <OccupancyPanel summary={gridData.summary} />
              </Box>
            </Box>

            {/* Leyenda de colores del modo activo + timestamp de la consulta */}
            <Box sx={styles.legendRow}>
              <MapLegend mode={mapMode} brandColors={brandColors} zoneColors={zoneColors} />
              <DataFreshness generatedAt={gridData.generatedAt} onRecalculate={handleRefresh} />
            </Box>

            {/* Explica por qué el resto del mapa se ve atenuado. Va pegado a la
                grilla, no debajo: abajo del mapa quedaría fuera de pantalla. */}
            {highlightBrand && (
              <Typography variant='caption' sx={styles.highlightNote}>
                Resaltando <strong>{highlightBrand}</strong> · el resto de las posiciones se muestra atenuado
              </Typography>
            )}

            <LayoutGrid
              gridData={gridData}
              mode={mapMode}
              activeAisle={selectedAisle}
              brandColors={brandColors}
              zoneColors={zoneColors}
              highlightBrand={highlightBrand}
              onAisleClick={handleAisleClick}
              onCellClick={handleCellClick}
            />
          </Fragment>
        )}

      </StandardPage>
     
      {selectedAisle && appliedWarehouseId !== null && (
        <AisleDetailModal
          open={Boolean(selectedAisle)}
          aisleName={selectedAisle}
          warehouseId={appliedWarehouseId}
          brandColors={brandColors}
          zoneColors={zoneColors}
          highlightBrand={highlightBrand}
          mode={mapMode}
          onModeChange={setMapMode}
          onClose={() => setSelectedAisle(null)}
        />
      )}

      {productsCell && appliedWarehouseId !== null && (
        <ProductsModal
          open={Boolean(productsCell)}
          warehouseId={appliedWarehouseId}
          cell={productsCell}
          brandColors={brandColors}
          onClose={() => setProductsCell(null)}
        />
      )}

      <SnackBar message={message} status={isSuccess ? 'success' : 'error'} open={isActive} />
    </Fragment>
  )
}


/**
 *
 * Función para recalcular los datos del mapa
 * en caso de actualizaciones de información
 * limpia el caché.
 *
 */
function DataFreshness({
  generatedAt,
  onRecalculate,
}: {
  generatedAt?: string
  onRecalculate: () => void
}) {
  const [now, setNow] = useState(() => Date.now())
  const [lastRefresh, setLastRefresh] = useState(readLastRefresh)

  const cooldown = Math.max(
    0,
    REFRESH_COOLDOWN_SECONDS - Math.floor((now - lastRefresh) / 1000)
  )

  // Un solo temporizador: por segundo mientras corre el cooldown, cada 30 s
  // después, que es lo que la antigüedad necesita para no quedar congelada.
  useEffect(() => {
    const t = setTimeout(() => setNow(Date.now()), cooldown > 0 ? 1000 : 30000)
    return () => clearTimeout(t)
  }, [now, cooldown])

  // Distingue este botón de BUSCAR y, durante el cooldown, explica por qué
  // está deshabilitado.
  const tooltip =
    cooldown > 0
      ? `Disponible en ${formatCooldown(cooldown)}. El recálculo se permite cada 5 minutos.`
      : 'Vuelve a calcular el mapa desde el inventario actual. BUSCAR sólo aplica los filtros y puede leer datos ya calculados.'

  const handleClick = () => {
    const ts = Date.now()
    localStorage.setItem(REFRESH_TS_KEY, String(ts))
    setLastRefresh(ts)
    setNow(ts)
    onRecalculate()
  }

  return (
    <Box sx={styles.freshnessRow}>
      {generatedAt && (
        <Typography
          variant='caption'
          color='text.secondary'
          title={new Date(generatedAt).toLocaleString('es-CL')}
          sx={styles.freshnessCaption}
        >
          Generado {formatAge(generatedAt, now)}
        </Typography>
      )}
      <Tooltip title={tooltip} placement='top' arrow>
        <Box component='span' sx={styles.tooltipWrapper}>
          <Button
            size='small'
            variant='outlined'
            color='primary'
            startIcon={<RefreshIcon sx={styles.refreshIcon} />}
            onClick={handleClick}
            disabled={cooldown > 0}
            sx={styles.refreshButton}
          >
            {cooldown > 0 ? `Recalcular (${formatCooldown(cooldown)})` : 'Recalcular'}
          </Button>
        </Box>
      </Tooltip>
    </Box>
  )
}

function OccupancyPanel({ summary }: { summary: GridResponse['summary'] }) {
  const divider = <Box sx={styles.occupancyDivider} />

  return (
    <Box sx={styles.occupancyPanel}>
      <Box sx={styles.occupancyCell}>
        <Typography sx={styles.statLabel}>Ocupadas</Typography>
        <Typography sx={styles.statValue}>{summary.occupied.toLocaleString('es-CL')}</Typography>
      </Box>
      {divider}
      <Box sx={styles.occupancyCell}>
        <Typography sx={styles.statLabel}>Vacías</Typography>
        <Typography sx={styles.statValue}>{summary.empty.toLocaleString('es-CL')}</Typography>
      </Box>
      {divider}
      <Box sx={styles.occupancyCell}>
        <Typography sx={styles.statLabel}>Ocupación</Typography>
        <Typography sx={styles.statValue}>{summary.occupancyPct.toFixed(1)}%</Typography>
      </Box>
    </Box>
  )
}
